import React from "react";
import { Box } from "@mui/material";
import logo from "../../../assets/logo.png";
import imageHeader from "../../../assets/landing/image-header2.png";
import Row from "../../../components/Row";
import ButtonLoading from "../../../components/ButtonLoading";
import { colors } from "../../../utils/constants";
import { landingTokens } from "../theme";
import { openWhatsAppContact, goToLogin } from "../constants";

interface HeroProps {
  isMobile: boolean;
}

function Hero({ isMobile }: HeroProps) {
  const styles = {
    header: {
      background: landingTokens.gradient,
      maxWidth: isMobile ? "100%" : "auto",
      height: isMobile ? "80svh" : "80vh",
      display: "flex",
      position: "relative" as const,
      overflow: "hidden",
      paddingLeft: isMobile ? "16px" : "64px",
      paddingRight: isMobile ? "16px" : "64px",
      alignItems: "center",
      justifyContent: "center",
    },
    photoLayer: {
      position: "absolute" as const,
      inset: 0,
      display: isMobile ? "none" : "block",
      zIndex: 0,
      overflow: "hidden",
    },
    photoImg: {
      position: "absolute" as const,
      right: "-2%",
      bottom: 0,
      height: "108%",
      width: "auto" as const,
      maxWidth: "none",
      opacity: 0.42,
      filter: "grayscale(1) contrast(1.08) brightness(1.08)",
    },
    photoOverlay: {
      position: "absolute" as const,
      inset: 0,
      background: [
        "radial-gradient(ellipse 70% 60% at 50% 48%, rgba(0, 48, 140, 0.28) 0%, rgba(0, 70, 180, 0.55) 100%)",
        "linear-gradient(180deg, rgba(0, 55, 160, 0.35) 0%, rgba(0, 85, 200, 0.22) 45%, rgba(0, 40, 130, 0.5) 100%)",
      ].join(", "),
    },
    logo: {
      width: isMobile ? 132 : 200,
      objectFit: "contain" as const,
    },
    nav: {
      alignItems: "center",
      position: "absolute" as const,
      top: isMobile ? 16 : 28,
      left: isMobile ? 16 : 40,
      right: isMobile ? 16 : 40,
      width: "auto",
      zIndex: 2,
    },
    navActions: {
      width: "100%",
      justifyContent: "flex-end",
      display: "flex",
      alignItems: "center",
      gap: isMobile ? 8 : 12,
    },
    navGhost: {
      marginTop: 0,
      width: "auto",
      minWidth: 0,
      height: 42,
      padding: "0 16px",
      borderRadius: `${landingTokens.radius.button}px`,
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
    },
    navSolid: {
      marginTop: 0,
      width: "auto",
      minWidth: 0,
      height: 42,
      padding: "0 18px",
      borderRadius: `${landingTokens.radius.button}px`,
      color: "#0B3D8C",
      backgroundColor: colors.white,
      border: "none",
      display: isMobile ? "none" : "flex",
    },
    content: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center" as const,
      position: "relative" as const,
      zIndex: 1,
      width: "100%",
      maxWidth: "780px",
      margin: "0 auto",
      gap: isMobile ? "10px" : "12px",
    },
    headline: {
      color: colors.white,
      fontSize: isMobile ? "32px" : "52px",
      fontWeight: 600,
      lineHeight: 1.15,
      letterSpacing: "-0.035em",
      maxWidth: isMobile ? "100%" : "720px",
      margin: 0,
    },
    subtitle: {
      color: "rgba(255,255,255,0.82)",
      fontSize: isMobile ? "16px" : "18px",
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: "0.01em",
      maxWidth: "520px",
      margin: 0,
    },
    ctaRow: {
      display: "flex",
      flexDirection: isMobile ? ("column" as const) : ("row" as const),
      alignItems: "center",
      justifyContent: "center",
      gap: isMobile ? "10px" : "12px",
      marginTop: "4px",
      width: isMobile ? "100%" : "auto",
    },
    ctaPrimary: {
      margin: 0,
      width: isMobile ? "100%" : "auto",
      minWidth: isMobile ? 0 : 220,
      height: 52,
      padding: "0 28px",
      borderRadius: `${landingTokens.radius.button}px`,
      backgroundColor: colors.white,
      color: "#0B3D8C",
      border: "none",
    },
    ctaSecondary: {
      margin: 0,
      width: isMobile ? "100%" : "auto",
      minWidth: isMobile ? 0 : 220,
      height: 52,
      padding: "0 28px",
      borderRadius: `${landingTokens.radius.button}px`,
      backgroundColor: "rgba(255,255,255,0.08)",
      color: colors.white,
      border: "1.5px solid rgba(255,255,255,0.72)",
      boxShadow: "none",
    },
  };

  return (
    <Box
      sx={styles.header}
      style={{ height: isMobile ? "80svh" : "80vh" }}
    >
      <Box sx={styles.photoLayer}>
        <img src={imageHeader} style={styles.photoImg} alt="" />
        <Box sx={styles.photoOverlay} />
      </Box>

      <Row space style={styles.nav}>
        <img src={logo} style={styles.logo} alt="Logo" />

        <Row style={styles.navActions}>
          <ButtonLoading
            isDelete
            label="Acessar"
            className="landing-hover-lift"
            styleLabel={{ fontSize: 14, color: colors.white, fontWeight: 500 }}
            style={styles.navGhost}
            onClick={goToLogin}
          />
          <ButtonLoading
            label="Entrar em contato"
            className="landing-hover-lift"
            style={styles.navSolid}
            styleLabel={{ fontSize: 14, fontWeight: 600, color: "#0B3D8C" }}
            onClick={openWhatsAppContact}
          />
        </Row>
      </Row>

      <div style={styles.content}>
        <h1 style={styles.headline}>
          Gere seu certificado de participação em eventos com facilidade!
        </h1>

        <p style={styles.subtitle}>
          Organize o evento, registre a presença e emita certificados digitais
          em poucos cliques — para organizadores e participantes.
        </p>

        <div style={styles.ctaRow}>
          <ButtonLoading
            label="Acessar plataforma"
            className="landing-hover-lift"
            styleLabel={{ fontSize: 15, fontWeight: 600, color: "#0B3D8C" }}
            style={styles.ctaPrimary}
            onClick={goToLogin}
          />
          <ButtonLoading
            label="Entrar em contato"
            className="landing-hover-lift"
            styleLabel={{ fontSize: 15, fontWeight: 500, color: colors.white }}
            style={styles.ctaSecondary}
            onClick={openWhatsAppContact}
          />
        </div>
      </div>
    </Box>
  );
}

export default Hero;
